import React, { useState, useEffect, useContext } from "react";
import ImageImports from "../../../utils/ImageImports";
import classes from "./ManageDocumentGroups.module.css";
import Button from "../../../components/Button/Button";
import { useItemTypeCatalog } from "../../../customHooks/useItemTypeCatalog";
import { DiscussionGroupInterface } from "../../../utils/interface";
import ReportTypeListItem from "./ReportTypeListItem";
import AddNewDocumentGroupModal from "./AddNewDocumentGroupModal";
import NewGroupConfirmationModal from "./NewGroupConfirmationModal";
import DeletedGroupConfirmationModal from "./DeletedGroupConfirmationModal";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, sortableKeyboardCoordinates, arrayMove } from "@dnd-kit/sortable";
import { restrictToVerticalAxis, restrictToParentElement } from "@dnd-kit/modifiers";
import useWindowSize from "../../../customHooks/useWindowSize";
import Link from "../../../components/Link/Link";
import { useOutletContext } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { DiscussionModuleOutletInterface } from "../DiscussionModuleContainer";
import { Store } from "../../../Store";

const { Arrow } = ImageImports;

function ManageDiscussionGroups() {
  const navigate = useNavigate();
  const { isMobile, isTablet } = useWindowSize();
  const [showAddNewGroupModal, setShowAddNewGroupModal] = useState<boolean>(false);
  const [showNewGroupConfirmationModal, setShowNewGroupConfirmationModal] = useState<boolean>(false);
  const [showDeletedGroupConfirmationModal, setShowDeletedGroupConfirmationModal] = useState<DiscussionGroupInterface | null>(null);
  const [reportTypes, setReportTypes] = useState<DiscussionGroupInterface[]>([]);
  const [selectedReportType, setSelectedReportType] = useState<DiscussionGroupInterface | null>(null);
  const [openEdits, setOpenEdits] = useState<number>(0);
  const {selectedUserGroup} = useContext(Store);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const { documentModule, documentGroups, setDocumentGroups } = useOutletContext<DiscussionModuleOutletInterface>();

  const { reorder: reorderDocumentGroups } = useItemTypeCatalog();
  const {
    create: createPowerReportType,
    update: updatePowerReportType,
    del: deletePowerReportType,
  } = documentModule.documentGroupApi;

  useEffect(() => {
    setReportTypes(documentGroups);
  }, [documentGroups]);

  const addNewDocumentGroup = async (name: string) => {
    try {
      const newReportType = await createPowerReportType({
        name,
        ugId: selectedUserGroup.id,
      });
      setReportTypes([...reportTypes, newReportType]);
      setDocumentGroups([...reportTypes, newReportType]);
      setSelectedReportType(newReportType);
      setShowNewGroupConfirmationModal(true);
    } catch (error) {
      console.error(error);
    }
  };

  const savePowerReportType = async (reportType: DiscussionGroupInterface): Promise<void> => {
    const savedReportType = await updatePowerReportType(reportType);

    // replace this the corresponding reportType in the state
    // get the index of the saved reportType by the itemTypeId
    const index = reportTypes.findIndex((reportType) => reportType.id === savedReportType.id);
    // if we found the itemTypeId in reportTypes, remove 1 element at that index and add the saved reportType at index spot
    // then reset reportTypes in the state
    if (index > -1) {
      let tmpReportTypes = [...reportTypes];
      tmpReportTypes.splice(index, 1, savedReportType);
      setReportTypes(tmpReportTypes);
    }
  };



  const deleteReportType = async (deleteReportType: DiscussionGroupInterface): Promise<void> => {
    const wasSuccessfull = await deletePowerReportType(deleteReportType);

    // remove this report type from the ui
    if (wasSuccessfull) {
      const index = reportTypes.findIndex((reportType) => reportType.id === deleteReportType.id);

      // if we found the itemTypeId in reportTypes, remove 1 element at that index
      // then reset reportTypes in the state
      if (index > -1) {
        let tmpReportTypes = [...reportTypes];
        tmpReportTypes.splice(index, 1);
        setReportTypes(tmpReportTypes);
        setDocumentGroups(tmpReportTypes);
      }
      setShowDeletedGroupConfirmationModal(deleteReportType);
    }
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = reportTypes.findIndex(({ id }) => id === active.id);
      const newIndex = reportTypes.findIndex(({ id }) => id === over.id);
      let tmpReportTypes: DiscussionGroupInterface[] = arrayMove(reportTypes, oldIndex, newIndex);

      // now we need to update the orders..
      // loop through report types and reassign orders
      for (let i = 0, displayOrder = 1; i < tmpReportTypes.length; i++, displayOrder++) {
        tmpReportTypes[i].displayOrder = displayOrder;
      }
      // update the database too
      reorderDocumentGroups(tmpReportTypes.map(({ id, displayOrder }) => ({ id, displayOrder })));
      setReportTypes(tmpReportTypes);
    }
  };

  return (
    <div
      className={`${classes.manage_document_groups} ${classes.flex_column} ${isMobile ? classes.is_mobile : ""} ${isTablet ? classes.is_tablet : ""}`}
    >
      {showAddNewGroupModal && (
        <AddNewDocumentGroupModal shown={showAddNewGroupModal} onCreate={addNewDocumentGroup} onClose={() => setShowAddNewGroupModal(false)} />
      )}
      {showNewGroupConfirmationModal && (
        <NewGroupConfirmationModal
          shown={showNewGroupConfirmationModal}
          onClose={() => {
            setShowNewGroupConfirmationModal(false);
            setSelectedReportType(null);
          }}
          addNewGroup={() => {
            setShowNewGroupConfirmationModal(false);
            setShowAddNewGroupModal(true);
          }}
          addNewDocument={() => {
            setShowNewGroupConfirmationModal(false);
            navigate(`../${selectedReportType?.id}/adddiscussion`);
          }}
          reportType={selectedReportType}
        />
      )}
      {showDeletedGroupConfirmationModal && (
        <DeletedGroupConfirmationModal
          shown={showDeletedGroupConfirmationModal !== null}
          reportType={showDeletedGroupConfirmationModal}
          onClose={() => setShowDeletedGroupConfirmationModal(null)}
        />
      )}
      <div className={`${classes.intro} ${classes.flex_column}`}>
        <Link className={`${classes.back_container} ${classes.flex_row}`} to="..">
          <img src={Arrow} className="back-arrow" alt={`Back to ${documentModule.name}`} />
          Back to {documentModule.name}
        </Link>
        {/* {showWarning && (
          <Alert onClose={() => setShowWarning(false)} type={"warning"}>
            Any changes to the discussion categories below will be applied only to the selected Users’ Group. These changes won’t be reflected across all groups in the Users’ Group Portal.
          </Alert>
        )} */}
        <div className={`${classes.section_title_cta} ${classes.flex_row}`}>
          <div className={`${classes.section_title}`}>
            <span className={`${classes.section_heading}`}>Manage Discussion Categories for {selectedUserGroup.name}  </span>
            <span className={`${classes.section_description}`}>
            Any changes to the discussion categories below will be applied only to the selected Users’ Group. These changes won’t be reflected across all groups in the Users’ Group Portal.
            </span>
          </div>
          <div className={`${classes.cta_container}`}>
            <Button className={`darkBlue`} disabled={openEdits > 0} text="Add Discussion Category" onClick={() => setShowAddNewGroupModal(true)} />
          </div>
        </div>
      </div>

      <div className={`${classes.report_types_container} ${classes.flex_column}`}>
        <DndContext
          onDragEnd={handleDragEnd}
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        >
          <SortableContext strategy={verticalListSortingStrategy} items={reportTypes}>
            {reportTypes.map((reportType) => (
              <ReportTypeListItem
                reportType={reportType}
                key={reportType.id}
                onSave={savePowerReportType}                
                editToggled={(isEditing) => {
                  isEditing ? setOpenEdits(openEdits + 1) : setOpenEdits(openEdits - 1);
                }}
                onDelete={deleteReportType}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
}

export default ManageDiscussionGroups;
